import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { Calendar, Search, CalendarDays, Plus, Eye, EyeOff, ArrowUpDown, FileText, ChevronsDown, ChevronsRight, ChevronsUp, Flag, X, User, ChevronDown, Check, CalendarArrowDown } from 'lucide-react';
import { MultiSelect } from '@/components/ui/multi-select';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem } from '@/components/ui/command';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import { useItemsProjectsStrict } from '@/hooks/useItemsProjectsStrict';
import { ItemFilters, ActiveFunctions, DisplayMode, ItemCategory, ItemsSortBy, ItemsSortDirection } from '@/types/item';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useItemsActionPermissions, useItemsCategoryPermissions, useItemsColumnPermissions } from '@/hooks/useItemsPermissions';
import { useIsMobile } from '@/hooks/use-mobile';
import { gateBadgeColors, gateTextColors, type GateType } from '@/constants/gateColors';
import { useAccessibleStatuses } from '@/hooks/useAccessibleFilterOptions';
import { PLAN_TRANSLATIONS } from '@/constants/planTranslations';
import { globalFeatureFlags } from '@/lib/featureFlags';
import { useMilestoneTypes } from '@/hooks/milestones/useMilestoneTypes';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Checkbox } from '@/components/ui/checkbox';
import { formatDateForBackend } from '@/utils/dateFormatUtils';
import { KeyMilestoneGroup } from '@/lib/keyMilestones';

interface ItemsHeaderProps {
  selectedProject: string;
  onProjectChange: (value: string) => void;
  activeFunctions: ActiveFunctions;
  onActiveFunctionsChange: (functions: ActiveFunctions) => void;
  displayMode: DisplayMode;
  onDisplayModeChange: (mode: DisplayMode) => void;
  filters: ItemFilters;
  onFiltersChange: (filters: ItemFilters) => void;
  anchorDate: string; // ISO date string (YYYY-MM-DD)
  onAnchorDateChange: (date: string) => void;
  onAlignAnchorToStart?: () => void;
  alignAnchorDisabled?: boolean;
  alignAnchorActive?: boolean;
  showProjectColumn: boolean;
  onShowProjectColumnChange: (show: boolean) => void;
  showCategoryColumn?: boolean;
  onShowCategoryColumnChange?: (show: boolean) => void;
  groupByCategory?: boolean;
  onGroupByCategoryChange?: (group: boolean) => void;
  toolbarExtra?: React.ReactNode;
  
  
  isLoading?: boolean;
  hideProjectSelector?: boolean;
  hideStatusFilter?: boolean;
  canReorder?: boolean;
  reorderModeEnabled?: boolean;
  onReorderModeChange?: (enabled: boolean) => void;
  isCheckingPermissions?: boolean;
  onTogglePerformanceMonitor?: () => void;
  showPerformanceMonitor?: boolean;
  
  // Add button props for project-specific contexts
  showAddButton?: boolean;
  onAddItems?: () => void;

  // Key milestone group buttons
  keyMilestoneGroups?: KeyMilestoneGroup[];
  existingKeyGroupNames?: Set<string>;
  onCreateKeyGroup?: (group: KeyMilestoneGroup) => void;
  creatingKeyGroupKey?: string | null;

  
  // Timeline report props
  onTimelineReport?: () => void;
  
  // Context-aware filtering
  restrictedProjectIds?: string[];
  
  // Expand/Collapse controls for milestones
  onExpandAllMilestones?: () => void;
  onCollapseAllMilestones?: () => void;
  expandedItemsCount?: number;
  totalItemsWithMilestones?: number;
  
  // Item type filter (for service projects)
  itemTypeFilter?: 'all' | 'build' | 'service';
  onItemTypeFilterChange?: (filter: 'all' | 'build' | 'service') => void;
  showItemTypeFilter?: boolean;
  
  // Hide sections
  hideFunctionsSection?: boolean;
  compactToolbar?: boolean; // Gộp cụm filter vào hàng toolbar, ẩn nhãn Anchor Date
  hideAnchorLabel?: boolean;
  compactProjectSelector?: boolean; // Đưa dropdown chọn dự án lên hàng toolbar, thu nhỏ
  hideGatesSection?: boolean;
  hideSearchInput?: boolean;
  
  // Summary selection mode
  summarySelectionMode?: boolean;
  onSummarySelectionModeChange?: (enabled: boolean) => void;
  summaryMilestonesCount?: number;
  
  // Assignee filter (for service projects)
  showAssigneeFilter?: boolean;
  assigneeFilter?: string[];
  onAssigneeSelect?: (userId: string) => void;
  onRemoveAssignee?: (userId: string) => void;
  onClearAssigneeFilter?: () => void;
  teamMembers?: { id: string; full_name: string; avatar_url?: string | null; email?: string }[];
}

const ItemsHeader = ({
  selectedProject,
  onProjectChange,
  activeFunctions,
  onActiveFunctionsChange,
  displayMode,
  onDisplayModeChange,
  filters,
  onFiltersChange,
  anchorDate,
  onAnchorDateChange,
  onAlignAnchorToStart,
  alignAnchorDisabled = false,
  alignAnchorActive = false,
  showProjectColumn,
  onShowProjectColumnChange,
  showCategoryColumn = false,
  onShowCategoryColumnChange,
  groupByCategory = false,
  onGroupByCategoryChange,
  toolbarExtra,
  
  
  isLoading = false,
  hideProjectSelector = false,
  hideStatusFilter = false,
  canReorder = false,
  reorderModeEnabled = false,
  onReorderModeChange,
  isCheckingPermissions = false,
  onTogglePerformanceMonitor,
  showPerformanceMonitor = false,
  
  showAddButton = false,
  onAddItems,
  keyMilestoneGroups,
  existingKeyGroupNames,
  onCreateKeyGroup,
  creatingKeyGroupKey = null,
  
  onTimelineReport,
  
  restrictedProjectIds,
  
  onExpandAllMilestones,
  onCollapseAllMilestones,
  expandedItemsCount = 0,
  totalItemsWithMilestones = 0,
  
  itemTypeFilter = 'all',
  onItemTypeFilterChange,
  showItemTypeFilter = false,
  
  hideFunctionsSection = false,
  compactToolbar = false,
  hideAnchorLabel = false,
  compactProjectSelector = false,
  hideGatesSection = false,
  hideSearchInput = false,
  
  summarySelectionMode = false,
  onSummarySelectionModeChange,
  summaryMilestonesCount = 0,
  
  showAssigneeFilter = false,
  assigneeFilter = [],
  onAssigneeSelect,
  onRemoveAssignee,
  onClearAssigneeFilter,
  teamMembers = []
}: ItemsHeaderProps) => {
  const { data: projects = [] } = useItemsProjectsStrict();
  const { canEditBasic } = useItemsActionPermissions();
  const { allowedCategories } = useItemsCategoryPermissions();
  const columnPermissions = useItemsColumnPermissions();
  const isMobile = useIsMobile();
  const { data: accessibleStatuses = [] } = useAccessibleStatuses(restrictedProjectIds);
  
  // ✅ HARDCODED (2025-11-09): Milestone feature always enabled
  const milestoneFeatureEnabled = true;
  const { data: milestoneTypes = [] } = useMilestoneTypes({ enabled: milestoneFeatureEnabled });

  // Preserved for prop-interface stability (Eye/EyeOff button removed intentionally)
  void showProjectColumn;
  void onShowProjectColumnChange;
  
  // Debug logging removed in Phase 8 cleanup

  // Frontend Safety: Filter projects by restrictedProjectIds
  const displayProjects = restrictedProjectIds 
    ? projects.filter(p => restrictedProjectIds.includes(p.id))
    : projects;

  // Auto-reset invalid selection
  useEffect(() => {
    if (restrictedProjectIds && selectedProject && !restrictedProjectIds.includes(selectedProject)) {
      if (import.meta.env.DEV) {
        console.info('[ItemsHeader] Invalid project selection cleared:', selectedProject);
      }
      onProjectChange('');
    }
  }, [restrictedProjectIds, selectedProject, onProjectChange]);

  const handleFunctionToggle = (functionType: keyof ActiveFunctions) => {
    onActiveFunctionsChange({
      ...activeFunctions,
      [functionType]: !activeFunctions[functionType]
    });
  };

  const generateMonthOptions = () => {
    const options = [];
    const currentDate = new Date();
    for (let i = -12; i <= 12; i++) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth() + i, 1);
      options.push({
        value: `${date.getFullYear()}-${date.getMonth() + 1}`,
        label: `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`,
        year: date.getFullYear(),
        month: date.getMonth() + 1
      });
    }
    return options;
  };

  const monthOptions = generateMonthOptions();

  const milestoneCluster = (
    <>
      {/* Milestone cluster: siblings within the merged row */}
      {milestoneFeatureEnabled && milestoneTypes.length > 0 && (
        <>
          {/* Milestone Types dropdown */}
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="h-9 justify-between w-[180px]">
                <span className="truncate">
                  {(() => {
                    const n = filters.activeMilestoneTypes?.length || 0;
                    if (n === 0) return 'Loại mốc';
                    if (n === milestoneTypes.length) return `Tất cả (${n})`;
                    return `Loại mốc (${n})`;
                  })()}
                </span>
                <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[280px] p-0" align="start">
              <Command>
                <CommandInput placeholder="Tìm loại mốc..." />
                <div className="flex gap-1 p-2 border-b">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="flex-1 h-7 text-xs"
                    onClick={() => onFiltersChange({
                      ...filters,
                      activeMilestoneTypes: milestoneTypes.map(t => t.name)
                    })}
                  >
                    Chọn tất cả
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="flex-1 h-7 text-xs"
                    onClick={() => onFiltersChange({
                      ...filters,
                      activeMilestoneTypes: []
                    })}
                  >
                    Bỏ chọn
                  </Button>
                </div>
                <CommandEmpty>Không tìm thấy.</CommandEmpty>
                <CommandGroup className="max-h-[300px] overflow-y-auto">
                  {milestoneTypes.map((type) => {
                    const isActive = filters.activeMilestoneTypes?.includes(type.name) || false;
                    return (
                      <CommandItem
                        key={type.name}
                        value={type.display_name}
                        onSelect={() => {
                          const currentTypes = filters.activeMilestoneTypes || [];
                          const newTypes = currentTypes.includes(type.name)
                            ? currentTypes.filter(t => t !== type.name)
                            : [...currentTypes, type.name];
                          onFiltersChange({ ...filters, activeMilestoneTypes: newTypes });
                        }}
                      >
                        <Check className={cn('mr-2 h-4 w-4', isActive ? 'opacity-100' : 'opacity-0')} />
                        <div
                          className="w-2.5 h-2.5 rounded-full border border-white/30 mr-2 shrink-0"
                          style={{ backgroundColor: type.color }}
                        />
                        <span className="truncate">{type.display_name}</span>
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              </Command>
            </PopoverContent>
          </Popover>

          {/* Milestone name search */}
          <div className="relative w-[220px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Tìm mốc theo tên…"
              value={filters.milestoneName || ''}
              onChange={(e) => onFiltersChange({
                ...filters,
                milestoneName: e.target.value.slice(0, 100)
              })}
              className="pl-9 pr-9 h-9 text-sm"
              maxLength={100}
            />
            {filters.milestoneName && (
              <Button
                variant="ghost"
                size="sm"
                className="absolute right-1 top-1/2 -translate-y-1/2 px-2 h-7"
                onClick={() => onFiltersChange({ ...filters, milestoneName: '' })}
              >
                <span className="text-lg leading-none">×</span>
              </Button>
            )}
          </div>

          {/* Expand/Collapse toggle */}
          {onExpandAllMilestones && onCollapseAllMilestones && (() => {
            const allExpanded = totalItemsWithMilestones > 0 && expandedItemsCount === totalItemsWithMilestones;
            return (
              <Button
                variant="outline"
                size="sm"
                className="h-9 w-9 p-0"
                disabled={!totalItemsWithMilestones || reorderModeEnabled}
                onClick={() => allExpanded ? onCollapseAllMilestones() : onExpandAllMilestones()}
                title={allExpanded ? 'Thu tất cả' : 'Mở tất cả'}
              >
                {allExpanded ? <ChevronsUp className="h-4 w-4" /> : <ChevronsDown className="h-4 w-4" />}
              </Button>
            );
          })()}
        </>
      )}
    </>
  );

  const sortIsCustom = !filters.sortBy || filters.sortBy === 'custom';

  const filterCluster = (
    <>
              <div className={`flex ${isMobile ? 'flex-col w-full' : 'items-center'} gap-2`}>
                {!compactToolbar && !hideAnchorLabel && (
                  <div className="flex items-center gap-2">
                    <CalendarDays className="h-4 w-4 text-muted-foreground" />
                    <span className={`${isMobile ? 'text-xs' : 'text-sm'} text-muted-foreground`}>{PLAN_TRANSLATIONS.anchorDate}:</span>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          isMobile ? 'w-full' : 'w-40',
                          "justify-start text-left font-normal",
                          !anchorDate && "text-muted-foreground"
                        )}
                      >
                        <CalendarDays className="mr-2 h-4 w-4" />
                        {anchorDate ? format(new Date(anchorDate), "dd/MM/yyyy") : <span>Chọn ngày</span>}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto min-w-[320px] p-0" align="start">
                      <CalendarComponent
                        mode="single"
                        selected={anchorDate ? new Date(anchorDate) : undefined}
                        onSelect={(date) => {
                          if (date) {
                            onAnchorDateChange(formatDateForBackend(date)!);
                          }
                        }}
                        initialFocus
                        className="p-3 pointer-events-auto"
                      />
                    </PopoverContent>
                  </Popover>
                  {onAlignAnchorToStart && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant={alignAnchorActive ? 'default' : 'outline'}
                          size="sm"
                          className="h-9 w-9 p-0 shrink-0"
                          disabled={alignAnchorDisabled}
                          onClick={onAlignAnchorToStart}
                        >
                          <CalendarArrowDown className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        Canh khung về đầu dự án (sớm hơn 1 tuần)
                      </TooltipContent>
                    </Tooltip>
                  )}
                </div>
              </div>
    
              {/* Category Filter & Group Toggle */}
              <div className={`flex ${isMobile ? 'flex-col w-full' : 'items-center'} gap-2`}>
                <div className={`flex items-center gap-2 ${isMobile ? 'flex-wrap w-full' : ''}`}>
                  {/* Multi-select Category Filter */}
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className={cn(isMobile ? 'w-auto max-w-full' : 'w-44', 'justify-between')}>
                        {filters.categories && filters.categories.length > 0 && filters.categories.length < allowedCategories.length
                          ? `${filters.categories.length} danh mục`
                          : 'Tất cả danh mục'}
                        <ChevronDown className="h-4 w-4 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-48 p-2" align="start">
                      <div className="space-y-2">
                        {/* Select All / Unselect All */}
                        <div className="flex items-center justify-end pb-2 border-b">
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="h-6 px-2 text-xs"
                            onClick={() => {
                              // undefined = all, [] = none
                              const isAllSelected = filters.categories === undefined;
                              onFiltersChange({ 
                                ...filters, 
                                categories: isAllSelected ? [] : undefined,
                                category: undefined
                              });
                            }}
                          >
                            {filters.categories === undefined ? 'Unselect all' : 'Select all'}
                          </Button>
                        </div>
                        {allowedCategories.map(category => {
                          // undefined = all selected, array = only those in array
                          const isChecked = filters.categories === undefined || filters.categories.includes(category);
                          return (
                            <div key={category} className="flex items-center space-x-2">
                              <Checkbox 
                                id={`cat-${category}`}
                                checked={isChecked}
                                onCheckedChange={(checked) => {
                                  // Start from current state
                                  const current = filters.categories === undefined 
                                    ? [...allowedCategories] // All selected → expand to array
                                    : [...filters.categories];
                                  
                                  let updated: ItemCategory[];
                                  if (checked) {
                                    updated = current.includes(category) ? current : [...current, category];
                                  } else {
                                    updated = current.filter(c => c !== category);
                                  }
                                  
                                  // If all selected → normalize to undefined
                                  const finalCategories = updated.length === allowedCategories.length 
                                    ? undefined 
                                    : updated;
                                  
                                  onFiltersChange({ 
                                    ...filters, 
                                    categories: finalCategories,
                                    category: undefined
                                  });
                                }}
                              />
                              <Label htmlFor={`cat-${category}`} className="text-sm cursor-pointer">{category}</Label>
                            </div>
                          );
                        })}
                      </div>
                    </PopoverContent>
                  </Popover>
                  
                  {milestoneCluster}
                  
                  {onGroupByCategoryChange && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className={`flex items-center gap-2 px-2 py-1 border rounded-md ${isMobile ? 'text-xs' : ''}`}>
                          <span className={`${isMobile ? 'text-xs' : 'text-sm'}`}>Group</span>
                          <Switch
                            checked={groupByCategory}
                            onCheckedChange={onGroupByCategoryChange}
                          />
                        </div>
                      </TooltipTrigger>
                      <TooltipContent>
                        {groupByCategory ? 'Tắt nhóm theo danh mục' : 'Bật nhóm theo danh mục để sắp xếp items theo categories'}
                      </TooltipContent>
                    </Tooltip>
                  )}
                  {toolbarExtra}
                  
                  {/* Sort Controls - Desktop */}
                  <div className="hidden md:flex items-center gap-2 px-2 py-1 border rounded-md">
                    {!compactToolbar && <span className="text-sm mr-1">Sort:</span>}
                    
                    {/* Custom Sort Button */}
                    <Button
                      variant={filters.sortBy === 'custom' || !filters.sortBy ? 'default' : 'outline'}
                      size="sm"
                      className="h-7 px-2 text-xs"
                      onClick={() => onFiltersChange({ ...filters, sortBy: 'custom' })}
                    >
                      Custom
                    </Button>
                    
                    {/* Start Sort Button with Direction */}
                    <div className="flex items-center gap-0">
                      <Button
                        variant={filters.sortBy === 'start' ? 'default' : 'outline'}
                        size="sm"
                        className="h-7 px-2 text-xs rounded-r-none"
                        onClick={() => onFiltersChange({ ...filters, sortBy: 'start' })}
                      >
                        Start
                      </Button>
                      <Button
                        variant={filters.sortBy === 'start' ? 'default' : 'outline'}
                        size="sm"
                        className="h-7 px-1.5 text-xs rounded-l-none border-l-0"
                        onClick={() => {
                          if (filters.sortBy === 'start') {
                            onFiltersChange({ 
                              ...filters, 
                              sortDirection: filters.sortDirection === 'asc' ? 'desc' : 'asc' 
                            });
                          } else {
                            onFiltersChange({ ...filters, sortBy: 'start', sortDirection: 'asc' });
                          }
                        }}
                        disabled={filters.sortBy !== 'start'}
                      >
                        {filters.sortBy === 'start' && filters.sortDirection === 'desc' ? '▼' : '▲'}
                      </Button>
                    </div>
                    
                    {/* End Sort Button with Direction */}
                    <div className="flex items-center gap-0">
                      <Button
                        variant={filters.sortBy === 'end' ? 'default' : 'outline'}
                        size="sm"
                        className="h-7 px-2 text-xs rounded-r-none"
                        onClick={() => onFiltersChange({ ...filters, sortBy: 'end' })}
                      >
                        End
                      </Button>
                      <Button
                        variant={filters.sortBy === 'end' ? 'default' : 'outline'}
                        size="sm"
                        className="h-7 px-1.5 text-xs rounded-l-none border-l-0"
                        onClick={() => {
                          if (filters.sortBy === 'end') {
                            onFiltersChange({ 
                              ...filters, 
                              sortDirection: filters.sortDirection === 'asc' ? 'desc' : 'asc' 
                            });
                          } else {
                            onFiltersChange({ ...filters, sortBy: 'end', sortDirection: 'asc' });
                          }
                        }}
                        disabled={filters.sortBy !== 'end'}
                      >
                        {filters.sortBy === 'end' && filters.sortDirection === 'desc' ? '▼' : '▲'}
                      </Button>
                    </div>
                  </div>
                  
                  {/* Sort Controls - Mobile Dropdown */}
                  <Select
                    value={`${filters.sortBy || 'custom'}-${filters.sortDirection || 'asc'}`}
                    onValueChange={(value) => {
                      const [sortBy, sortDirection] = value.split('-') as [ItemsSortBy, ItemsSortDirection];
                      onFiltersChange({ ...filters, sortBy, sortDirection });
                    }}
                  >
                    <SelectTrigger className="md:hidden w-auto min-w-[110px] h-8 text-xs">
                      <SelectValue placeholder="Sort" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="custom-asc">Custom Order</SelectItem>
                      <SelectItem value="start-asc">Start Date ▲</SelectItem>
                      <SelectItem value="start-desc">Start Date ▼</SelectItem>
                      <SelectItem value="end-asc">End Date ▲</SelectItem>
                      <SelectItem value="end-desc">End Date ▼</SelectItem>
                    </SelectContent>
                  </Select>
    
                  {/* Performance Monitor Toggle */}
                  {onTogglePerformanceMonitor && import.meta.env.VITE_ITEMS_PERF_MONITOR === 'true' && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className="flex items-center space-x-2">
                          <span className="text-sm font-medium">Performance</span>
                          <Switch
                            checked={showPerformanceMonitor}
                            onCheckedChange={onTogglePerformanceMonitor}
                          />
                        </div>
                      </TooltipTrigger>
                      <TooltipContent>
                        {showPerformanceMonitor ? 'Ẩn monitor hiệu suất' : 'Hiển thị monitor hiệu suất'}
                      </TooltipContent>
                    </Tooltip>
                  )}
                </div>
              </div>
    </>
  );


  const itemTypeControl = (
    <>
      {showItemTypeFilter && (
        <Select
          value={itemTypeFilter}
          onValueChange={(v) => onItemTypeFilterChange?.(v as 'all' | 'build' | 'service')}
        >
          <SelectTrigger className="w-[180px] h-9">
            <SelectValue placeholder="Loại việc" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tất cả loại</SelectItem>
            <SelectItem value="build">Build</SelectItem>
            <SelectItem value="service">Service</SelectItem>
          </SelectContent>
        </Select>
      )}
    </>
  );

  const assigneeControl = (
    <>
      {showAssigneeFilter && (
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <Select onValueChange={onAssigneeSelect} value="">
              <SelectTrigger className="w-[160px] h-9">
                <User className="h-4 w-4 mr-1 text-muted-foreground" />
                <SelectValue placeholder="Người phụ trách..." />
              </SelectTrigger>
              <SelectContent>
                {teamMembers.map((member) => (
                  <SelectItem
                    key={member.id}
                    value={member.id}
                    disabled={assigneeFilter.includes(member.id)}
                  >
                    <div className="flex items-center gap-2">
                      <Avatar className="h-5 w-5">
                        <AvatarImage src={member.avatar_url || undefined} />
                        <AvatarFallback className="text-xs">
                          {member.full_name?.charAt(0)?.toUpperCase() || '?'}
                        </AvatarFallback>
                      </Avatar>
                      {member.full_name}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Selected assignees as badges */}
          {assigneeFilter.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {assigneeFilter.map((userId) => {
                const member = teamMembers.find(m => m.id === userId);
                return (
                  <Badge
                    key={userId}
                    variant="secondary"
                    className="flex items-center gap-1"
                  >
                    {member?.full_name || 'Unknown'}
                    <button
                      type="button"
                      onClick={() => onRemoveAssignee?.(userId)}
                      className="ml-1 hover:text-destructive"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                );
              })}
              <Button
                variant="ghost"
                size="sm"
                onClick={onClearAssigneeFilter}
                className="h-6 px-2 text-xs"
              >
                Xóa
              </Button>
            </div>
          )}
        </div>
      )}
    </>
  );

  const projectSelectorControl = (
    <>
      {!hideProjectSelector && (
        compactProjectSelector ? (
          <Select value={selectedProject || "all"} onValueChange={(value) => onProjectChange(value === "all" ? "" : value)} disabled={isLoading}>
            <SelectTrigger className="w-[220px] h-9">
              <SelectValue placeholder={PLAN_TRANSLATIONS.selectProject} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{PLAN_TRANSLATIONS.allProjects}</SelectItem>
              {displayProjects.map((project) => (
                <SelectItem key={project.id} value={project.id}>
                  {project.project_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <div className={isMobile ? 'w-full' : 'flex-1'}>
            <Select value={selectedProject || "all"} onValueChange={(value) => onProjectChange(value === "all" ? "" : value)} disabled={isLoading}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder={PLAN_TRANSLATIONS.selectProject} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{PLAN_TRANSLATIONS.allProjects}</SelectItem>
                {displayProjects.map((project) => (
                  <SelectItem key={project.id} value={project.id}>
                    {project.project_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )
      )}
    </>
  );

  return (
    <TooltipProvider>
    <div className="items-header space-y-4">
        {/* ⭐ Merged toolbar row: actions + filter dropdowns */}
        <div className="flex flex-wrap items-center gap-2">
            {compactProjectSelector && projectSelectorControl}

            {/* Create Items Button - Only show in project-specific contexts */}
            {showAddButton && onAddItems && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="default"
                    size="sm"
                    onClick={onAddItems}
                    disabled={!canEditBasic}
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  {canEditBasic ? PLAN_TRANSLATIONS.createItemsTooltip : PLAN_TRANSLATIONS.noPermissionCreate}
                </TooltipContent>
              </Tooltip>
            )}

            {keyMilestoneGroups && onCreateKeyGroup && keyMilestoneGroups.map((group) => {
              const already = existingKeyGroupNames?.has(group.itemName)
                || group.legacyNames.some(n => existingKeyGroupNames?.has(n));
              const isCreating = creatingKeyGroupKey === group.key;
              return (
                <Button
                  key={group.key}
                  variant="outline"
                  size="sm"
                  className="h-9"
                  disabled={!canEditBasic || already || creatingKeyGroupKey !== null}
                  title={already ? `Đã có nhóm "${group.itemName}"` : !canEditBasic ? 'Không có quyền' : `Tạo nhóm mốc ${group.itemName}`}
                  onClick={() => onCreateKeyGroup(group)}
                >
                  {isCreating ? 'Đang tạo…' : `+ ${group.itemName}`}
                </Button>
              );
            })}


            {/* Summary Selection Mode Toggle - Compact */}
            {onSummarySelectionModeChange && (
              <div className="flex items-center gap-2 px-2 h-9 border rounded-md bg-muted/20">
                <Flag className="h-3 w-3 text-primary" />
                <span className="text-xs">Chọn mốc</span>
                {summaryMilestonesCount > 0 && (
                  <Badge variant="secondary" className="text-[10px] h-4 px-1">
                    {summaryMilestonesCount}
                  </Badge>
                )}
                <Switch 
                  checked={summarySelectionMode}
                  onCheckedChange={onSummarySelectionModeChange}
                  className="scale-75"
                />
              </div>
            )}

            {columnPermissions.category && onShowCategoryColumnChange && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onShowCategoryColumnChange(!showCategoryColumn)}
                  >
                    <span className="text-xs">Cat</span>
                    {showCategoryColumn ? <Eye className="h-3 w-3 ml-1" /> : <EyeOff className="h-3 w-3 ml-1" />}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  {showCategoryColumn ? PLAN_TRANSLATIONS.hideCategories : PLAN_TRANSLATIONS.showCategories}
                </TooltipContent>
              </Tooltip>
            )}

            {/* Reorder Mode Toggle - Only show when sortBy = custom */}
            {canReorder && onReorderModeChange && (compactToolbar || sortIsCustom) && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className={`flex items-center gap-2 px-3 h-9 border rounded-md ${isMobile ? 'text-xs' : ''}${!sortIsCustom ? ' opacity-50' : ''}`}>
                    <ArrowUpDown className="h-4 w-4" />
                    <span className={`${isMobile ? 'text-xs' : 'text-sm'}`}>{PLAN_TRANSLATIONS.reorder}</span>
                    <Switch
                      checked={reorderModeEnabled}
                      onCheckedChange={onReorderModeChange}
                      disabled={!sortIsCustom}
                    />
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  {!sortIsCustom ? 'Chỉ sắp xếp thủ công khi Sort = Custom' : (reorderModeEnabled ? PLAN_TRANSLATIONS.reorderModeOn : PLAN_TRANSLATIONS.reorderModeOff)}
                </TooltipContent>
              </Tooltip>
            )}

            {/* Show loading state while checking permissions */}
            {isCheckingPermissions && (
              <div className={`flex items-center gap-2 px-3 h-9 border rounded-md opacity-75 ${isMobile ? 'text-xs' : ''}`}>
                <ArrowUpDown className="h-4 w-4" />
                <span className={`${isMobile ? 'text-xs' : 'text-sm'}`}>{PLAN_TRANSLATIONS.checkingPermissions}</span>
                <Switch
                  checked={false}
                  disabled
                />
              </div>
            )}

            {/* Show disabled reorder toggle with tooltip if no permission */}
            {!canReorder && !isCheckingPermissions && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className={`flex items-center gap-2 px-3 h-9 border rounded-md opacity-50 cursor-not-allowed ${isMobile ? 'text-xs' : ''}`}>
                    <ArrowUpDown className="h-4 w-4" />
                    <span className={`${isMobile ? 'text-xs' : 'text-sm'}`}>{PLAN_TRANSLATIONS.reorder}</span>
                    <Switch
                      checked={false}
                      disabled
                    />
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  {PLAN_TRANSLATIONS.noPermissionReorder}
                </TooltipContent>
              </Tooltip>
            )}

            {/* Function Toggles */}
            {!hideFunctionsSection && (
              <MultiSelect
                options={[
                  { value: 'shop', label: 'Shop' },
                  { value: 'specs', label: 'Specs' },
                  { value: 'po', label: 'PO' },
                  { value: 'contract', label: 'Contract' },
                ]}
                value={(['shop','specs','po','contract'] as const).filter(k => activeFunctions[k])}
                onChange={(vals) => {
                  const next: ActiveFunctions = {
                    shop: vals.includes('shop'),
                    specs: vals.includes('specs'),
                    po: vals.includes('po'),
                    contract: vals.includes('contract'),
                  };
                  onActiveFunctionsChange(next);
                }}
                placeholder="Chức năng"
                className="w-[200px]"
              />
            )}

            {/* Item Type Filter - Only for Service Projects */}
            {!compactToolbar && itemTypeControl}

            {/* Gate Toggles */}
            {!hideGatesSection && (
              <MultiSelect
                options={[
                  { value: 'g1', label: 'G1' },
                  { value: 'g2', label: 'G2' },
                  { value: 'g3', label: 'G3' },
                  { value: 'g4', label: 'G4' },
                  { value: 'g5', label: 'G5' },
                ]}
                value={(['g1','g2','g3','g4','g5'] as const).filter(k => filters.activeGates?.[k])}
                onChange={(vals) => {
                  const nextGates = {
                    g1: vals.includes('g1'),
                    g2: vals.includes('g2'),
                    g3: vals.includes('g3'),
                    g4: vals.includes('g4'),
                    g5: vals.includes('g5'),
                  };
                  onFiltersChange({ ...filters, activeGates: nextGates });
                }}
                placeholder="Gate"
                className="w-[180px]"
              />
            )}


            {/* Assignee Filter - Moved into merged toolbar */}
            {!compactToolbar && assigneeControl}
            {compactToolbar && filterCluster}
            {compactToolbar && itemTypeControl}
            {compactToolbar && assigneeControl}
            {/* Timeline Report Button - Only show when handler is provided */}
            {onTimelineReport && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={onTimelineReport}
                  >
                    <FileText className="h-4 w-4 mr-1" />
                    {!isMobile && PLAN_TRANSLATIONS.timelineReport}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  {PLAN_TRANSLATIONS.timelineReportTooltip}
                </TooltipContent>
              </Tooltip>
            )}
        </div>

        {!compactProjectSelector && (!hideProjectSelector || !hideSearchInput) && (
        <div className={`${isMobile ? 'w-full space-y-3' : 'flex gap-3'}`}>
            {!hideProjectSelector && (
              <div className={isMobile ? 'w-full' : 'flex-1'}>
                <Select value={selectedProject || "all"} onValueChange={(value) => onProjectChange(value === "all" ? "" : value)} disabled={isLoading}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder={PLAN_TRANSLATIONS.selectProject} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{PLAN_TRANSLATIONS.allProjects}</SelectItem>
                    {displayProjects.map((project) => (
                      <SelectItem key={project.id} value={project.id}>
                        {project.project_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          
            {!hideSearchInput && (
              <div className={isMobile ? 'w-full relative' : 'flex-1 relative'}>
                {!isMobile && (
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                )}
                <Input
                  placeholder={PLAN_TRANSLATIONS.searchItems}
                  value={filters.search}
                  onChange={(e) => onFiltersChange({ ...filters, search: e.target.value })}
                  className={isMobile ? "pl-3" : "pl-10"}
                  disabled={isLoading}
                />
              </div>
            )}
        </div>
        )}




        {!compactToolbar && (
        <div className={`${isMobile ? 'space-y-3' : 'flex flex-col sm:flex-row gap-4 items-start sm:items-center'}`}>
          {filterCluster}
        </div>
        )}
    </div>
    </TooltipProvider>
  );
};

export default ItemsHeader;